'use client';

/* Hallmark · genre: editorial · macrostructure: Narrative Workflow — R8 · theme: custom (warm ledger) · design-system: R8 contract */

import Link from 'next/link';
import { Building2, Shield, Users, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LANDING_CONTENT } from '../constants/content';
import type { TrustPillar } from '../constants/content';
import LandingReveal from './LandingReveal';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import { PAPER_BAND, PAPER_INK, PAPER_RULE } from '../ui/paper-tokens';
import { LANDING_PRIMARY_CTA_RECIPE } from '../ui/cta-recipe';

const TRUST_ICON_MAP: Record<string, LucideIcon> = {
  Building2,
  Shield,
  Users,
};

/**
 * SetupSection — R8 §S (`.claude/handoffs/2026-08-12-r8-restructure-contract.md`)
 * "You do not receive an empty CRM." New chapter, band `PAPER_BAND.base` per
 * the contract's band ladder (Process base -> Brain alt -> Setup base ->
 * WhatsApp alt).
 *
 * Two duties merge here:
 *  1. The assisted-onboarding narrative itself — eyebrow + serif H2/sub, then
 *     4 QUIET steps reading `setup.steps` (num/title/caption, identical shape
 *     to `oneProcess.steps`). Albert's explicit instruction (contract §S):
 *     NOT big bubble/card blocks — small numeral + title + one caption line,
 *     no boxes-in-boxes.
 *
 *     ARRANGEMENT (2026-08-12, R8 review FIX 9): a 2x2 LEDGER of hairline-left
 *     rows, numeral inline on the title's own baseline. It used to be a
 *     1x4 divided strip with the numeral stacked above the title — the exact
 *     fingerprint `OneProcessSection` (§P) already owns, and, until FIX 4
 *     deleted it, `configTogether` owned a third copy of. Two numbered
 *     sequences survive on this page; they must not read as the same
 *     component twice, so §P keeps the horizontal centred rail (icon discs +
 *     mono numerals + connectors) and §S takes the quieter two-column ledger.
 *     Same ink, same hairline, same numeral copy — only the arrangement
 *     differs, which is the whole point (Albert asked for quieter, not for a
 *     second register). At the mobile floor it degrades to one column of the
 *     same rows, so the 320px case gains nothing to overflow.
 *     `ui/Connector.tsx` stays unused here: its SVG line is hardcoded
 *     `#FFFFFF1A` (dark-exhibit-only per its own header; invisible on light
 *     paper), so the rules here are `PAPER_RULE.hairline.border` instead.
 *  2. TrustBlockSection's carry-over duties (contract §S "TrustBlock duties
 *     that must SURVIVE the merge"): the three glance-level trust pillars +
 *     the public-prices honest line, now reading `setup.trust` — the SAME
 *     module-level `TRUST_PILLARS`/`TRUST_HONEST_LINE` constants
 *     TrustBlockSection.tsx itself reads (content.ts, single source, zero
 *     drift), rendered as a QUIET tail (smaller type, icon+dt/dd per fact,
 *     no full assurance register) below the CTA — not a duplicate of
 *     TrustBlockSection's own bigger two-column register, which this section
 *     replaces on the page. The legal-identifier line (CUI/Reg.Com.) is NOT
 *     re-rendered here: it lives exactly once in `footer.legalLine`
 *     (untouched — recon confirmed nothing to relocate), and the three legal
 *     document links (Terms/DPA/Cookies) already exist in `footer.legal`.
 *
 * CTA: amber `LANDING_PRIMARY_CTA_RECIPE` -> `/auth/register` (the canonical
 * CTA route), same recipe/geometry as `FinalCTASection.tsx`'s primary button.
 */
function TrustTailFact({ pillar }: { pillar: TrustPillar }) {
  const { t } = useLocale();
  const Icon = TRUST_ICON_MAP[pillar.icon] ?? Building2;

  return (
    <div className="flex flex-col gap-1">
      <dt
        className={cn(
          'flex items-center gap-1.5 text-[13px] font-semibold',
          PAPER_INK.primary.text
        )}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t(pillar.title)}
      </dt>
      <dd className={cn('text-[12px] leading-5', PAPER_INK.muted.text)}>{t(pillar.body)}</dd>
    </div>
  );
}

function SetupSectionInner() {
  const { setup } = LANDING_CONTENT;
  const { t } = useLocale();

  return (
    <section id="setup" className="relative py-20 lg:py-24 overflow-hidden">
      <div className={cn('absolute inset-0', PAPER_BAND.base.bg)} aria-hidden="true" />

      <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* ONE group reveal for the whole chapter (header + steps + CTA +
              trust tail) — no per-row cascade, same "reduced motion
              composition" discipline TrustBlockSection.tsx documents. */}
          <LandingReveal>
            {/* ── Header ── */}
            <div className="max-w-[54ch] mb-10 lg:mb-12">
              <div
                className={cn(
                  'inline-flex items-center h-7 px-3 rounded-full border mb-5',
                  PAPER_RULE.hairline.border,
                  'bg-black/[0.03]'
                )}
              >
                <span
                  className={cn(
                    'text-[11px] leading-4 font-semibold uppercase tracking-[0.16em]',
                    PAPER_INK.muted.text
                  )}
                >
                  {t(setup.eyebrow)}
                </span>
              </div>
              <h2
                className={cn(
                  'landing-serif-display mb-4 max-w-[20ch] text-[34px] leading-[1.15] lg:text-[40px] lg:leading-[1.1]',
                  PAPER_INK.primary.text
                )}
              >
                {t(setup.h2)}
              </h2>
              <p
                className={cn(
                  'max-w-[52ch] text-lg leading-[1.55] tracking-[-0.01em] lg:text-xl',
                  PAPER_INK.secondary.text
                )}
              >
                {t(setup.sub)}
              </p>
            </div>

            {/* ── 4 quiet steps as a 2x2 ledger of hairline-left rows (see
                the ARRANGEMENT note in the file header for why this is NOT
                §P's 1x4 strip). No card/box chrome around any step: the only
                chrome is one 1px rule down each row's left edge. ── */}
            <div
              data-slot="setup-ledger"
              className="grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2 lg:gap-x-14"
            >
              {setup.steps.map((step) => (
                <div
                  key={step.num}
                  data-slot="setup-step"
                  className={cn('border-l pl-5', PAPER_RULE.hairline.border)}
                >
                  {/* Numeral sits INLINE on the title's baseline (§P stacks
                      its numeral above an icon disc) — same tabular-nums
                      treatment, different anatomy. */}
                  <div className="flex items-baseline gap-2.5">
                    <span
                      className={cn(
                        'shrink-0 text-[12px] font-semibold leading-4 tabular-nums',
                        PAPER_INK.muted.text
                      )}
                    >
                      {step.num}
                    </span>
                    <h3 className={cn('text-base font-semibold', PAPER_INK.primary.text)}>
                      {t(step.title)}
                    </h3>
                  </div>
                  <p
                    className={cn(
                      'mt-1.5 max-w-[46ch] text-[14px] leading-[1.5]',
                      PAPER_INK.muted.text
                    )}
                  >
                    {t(step.caption)}
                  </p>
                </div>
              ))}
            </div>

            {/* ── CTA + helper — canonical CTA route, FinalCTASection's own
                button recipe/geometry (h-12 px-6 rounded-xl text-[15px]
                font-semibold). Left-aligned: Setup is a narrative chapter,
                not one of the page's two centered conversion poles
                (Hero/FinalCTA). ── */}
            <div className="mt-10 lg:mt-12 flex flex-col items-start gap-3">
              <Link
                href="/auth/register"
                className={cn(
                  'inline-flex h-12 items-center justify-center rounded-xl px-6 text-[15px] font-semibold',
                  LANDING_PRIMARY_CTA_RECIPE
                )}
              >
                {t(setup.cta)}
              </Link>
              <p className={cn('text-sm', PAPER_INK.muted.text)}>{t(setup.ctaHelper)}</p>
            </div>

            {/* ── Quiet trust tail — TrustBlockSection carry-over (see file
                doc comment). ── */}
            <div className={cn('mt-12 border-t pt-8 lg:mt-14', PAPER_RULE.hairline.border)}>
              <dl className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-3">
                {setup.trust.pillars.map((pillar) => (
                  <TrustTailFact key={pillar.icon} pillar={pillar} />
                ))}
              </dl>
              <p className={cn('mt-6 text-[13px] font-medium', PAPER_INK.secondary.text)}>
                {t(setup.trust.honestLine)}
              </p>
            </div>
          </LandingReveal>
        </div>
      </div>
    </section>
  );
}

export default function SetupSection() {
  return <SetupSectionInner />;
}
